// ============================================
// Component: SellerBundlesDiscounts (Field Agent Bundles & Discounts)
// Purpose: Display bundles and special offers for field agents (סוכנים)
// UX Notes:
// - Modern card-based layout with gradient backgrounds
// - Clear savings indicators with prominent badges
// - Responsive grid (mobile-first)
// - Loading states with skeletons
// - Empty states with helpful CTAs
// - Touch-friendly interactions (min 44x44px)
// ============================================

import React, { useState, useEffect } from 'react';
import axios from '../../utils/axios';
import { 
  TagIcon, 
  SparklesIcon,
  GiftIcon,
  TruckIcon,
  ClockIcon,
  ArrowRightIcon,
  ExclamationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const SellerBundlesDiscounts = () => {
  const [bundles, setBundles] = useState([]);
  const [specialOffers, setSpecialOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bundles'); // 'bundles' or 'offers'
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [showBundleDetails, setShowBundleDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch bundles (only B2B bundles for field agents)
      const bundlesResponse = await axios.get('/api/bundles?customer_type=b2b&limit=50');
      setBundles(bundlesResponse.data.bundles || []);

      // Fetch products with special offers (discount > 0)
      const productsResponse = await axios.get('/api/products?limit=50');
      const productsWithOffers = (productsResponse.data.products || []).filter(
        product => product.discount_percentage > 0 || product.special_price
      );
      setSpecialOffers(productsWithOffers);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('שגיאה בטעינת הנתונים');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleBundleDetails = async (bundle) => {
    try {
      // Fetch bundle products
      const response = await axios.get(`/api/bundles/${bundle.id}/products`);
      setSelectedBundle({
        ...bundle,
        products: response.data.products
      });
      setShowBundleDetails(true);
    } catch (error) {
      console.error('Error fetching bundle details:', error);
      toast.error('שגיאה בטעינת פרטי החבילה');
    }
  };

  const closeBundleDetails = () => {
    setShowBundleDetails(false);
    setSelectedBundle(null);
  };

  const calculateSavings = (regularPrice, bundlePrice) => {
    const savings = parseFloat(regularPrice) - parseFloat(bundlePrice);
    const percentage = ((savings / parseFloat(regularPrice)) * 100).toFixed(0);
    return { savings, percentage };
  };

  const getBadgeColor = (percentage) => {
    if (percentage >= 30) return 'bg-gradient-to-r from-red-500 to-pink-500';
    if (percentage >= 20) return 'bg-gradient-to-r from-orange-500 to-yellow-500';
    if (percentage >= 10) return 'bg-gradient-to-r from-blue-500 to-indigo-500';
    return 'bg-gradient-to-r from-green-500 to-emerald-500';
  };

  const getCustomerTypeBadge = (type) => {
    if (type === 'b2b') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <svg className="ml-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
          </svg>
          B2B
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
        <svg className="ml-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
        </svg>
        B2C
      </span>
    );
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="mb-8 animate-pulse">
            <div className="h-10 bg-gray-200 rounded-lg w-64 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-96"></div>
          </div>
          
          {/* Tabs Skeleton */}
          <div className="flex space-x-4 mb-8">
            <div className="h-12 bg-gray-200 rounded-lg w-32"></div>
            <div className="h-12 bg-gray-200 rounded-lg w-32"></div>
          </div>

          {/* Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-md p-6 animate-pulse">
                <div className="h-48 bg-gray-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl shadow-lg">
              <GiftIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                חבילות ומבצעים
              </h1>
              <p className="text-gray-600 mt-1">
                הצעות מיוחדות וחבילות חיסכון ללקוחות
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 mb-8 bg-white rounded-xl p-2 shadow-sm">
          <button
            onClick={() => setActiveTab('bundles')}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'bundles'
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <GiftIcon className="h-5 w-5" />
            <span>חבילות ({bundles.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('offers')}
            className={`flex-1 flex items-center justify-center space-x-2 py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'offers'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <SparklesIcon className="h-5 w-5" />
            <span>מבצעים ({specialOffers.length})</span>
          </button>
        </div>

        {/* Bundles Tab */}
        {activeTab === 'bundles' && (
          <>
            {bundles.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <GiftIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  אין חבילות זמינות כרגע
                </h3>
                <p className="text-gray-600">
                  חבילות חדשות יתווספו בקרוב
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {bundles.map(bundle => {
                  const { savings, percentage } = calculateSavings(
                    bundle.regular_price,
                    bundle.bundle_price
                  );

                  return (
                    <div
                      key={bundle.id}
                      className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                    >
                      {/* Image */}
                      <div className="relative h-56 bg-gradient-to-br from-purple-100 to-pink-100 overflow-hidden">
                        {bundle.bundle_image ? (
                          <img
                            src={bundle.bundle_image}
                            alt={bundle.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <GiftIcon className="h-24 w-24 text-purple-300" />
                          </div>
                        )}
                        
                        {/* Discount Badge */}
                        <div className={`absolute top-4 left-4 ${getBadgeColor(percentage)} text-white px-4 py-2 rounded-full shadow-lg font-bold text-lg`}>
                          חסכון {percentage}%
                        </div>

                        {/* Status Badge */}
                        {bundle.status && bundle.status !== 'active' && (
                          <div className="absolute top-4 right-4">
                            <span className={`px-3 py-1 text-white text-sm font-bold rounded-full shadow-lg ${
                              bundle.status === 'not_started' ? 'bg-blue-500' : 
                              bundle.status === 'expired' ? 'bg-red-500' : 'bg-gray-500'
                            }`}>
                              {bundle.status_message || bundle.status}
                            </span>
                          </div>
                        )}

                        {/* Customer Type Badge */}
                        <div className={`absolute ${bundle.status && bundle.status !== 'active' ? 'top-16' : 'top-4'} right-4`}>
                          {getCustomerTypeBadge(bundle.customer_type)}
                        </div>

                        {/* Featured Badge */}
                        {bundle.is_featured && (
                          <div className="absolute bottom-4 left-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1 shadow-lg">
                            <HeartSolidIcon className="h-4 w-4" />
                            <span>מומלץ</span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                          {bundle.title}
                        </h3>
                        
                        {bundle.description && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {bundle.description}
                          </p>
                        )}

                        {/* Products Count */}
                        <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
                          <TagIcon className="h-4 w-4" />
                          <span>{bundle.items?.length || 0} מוצרים בחבילה</span>
                        </div>

                        {/* Price Section */}
                        <div className="border-t border-gray-100 pt-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-gray-500 line-through">
                              {formatCurrency(bundle.regular_price)}
                            </span>
                            <span className="text-sm font-medium text-green-600">
                              חסכון של {formatCurrency(savings)}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-3xl font-bold text-gray-900">
                              {formatCurrency(bundle.bundle_price)}
                            </span>
                            <button 
                              onClick={() => handleBundleDetails(bundle)}
                              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                            >
                              <span>פרטים</span>
                              <ArrowRightIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Validity */}
                        {bundle.valid_until && (
                          <div className="mt-4 flex items-center space-x-2 text-xs text-orange-600 bg-orange-50 px-3 py-2 rounded-lg">
                            <ClockIcon className="h-4 w-4" />
                            <span>
                              בתוקף עד: {new Date(bundle.valid_until).toLocaleDateString('he-IL')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Special Offers Tab */}
        {activeTab === 'offers' && (
          <>
            {specialOffers.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <SparklesIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  אין מבצעים זמינים כרגע
                </h3>
                <p className="text-gray-600">
                  מבצעים חדשים יתווספו בקרוב
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {specialOffers.map(product => {
                  const regularPrice = parseFloat(product.price_b2c || product.price_b2b);
                  const specialPrice = parseFloat(product.special_price || regularPrice);
                  const savings = regularPrice - specialPrice;
                  const percentage = ((savings / regularPrice) * 100).toFixed(0);

                  return (
                    <div
                      key={product.id}
                      className="group bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-1"
                    >
                      {/* Image */}
                      <div className="relative h-48 bg-gradient-to-br from-orange-100 to-red-100 overflow-hidden">
                        {product.images && product.images[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <TagIcon className="h-16 w-16 text-orange-300" />
                          </div>
                        )}
                        
                        {/* Discount Badge */}
                        <div className={`absolute top-3 left-3 ${getBadgeColor(percentage)} text-white px-3 py-1.5 rounded-full shadow-lg font-bold text-sm`}>
                          -{percentage}%
                        </div>

                        {/* Stock Badge */}
                        {product.stock_quantity < 10 && product.stock_quantity > 0 && (
                          <div className="absolute bottom-3 left-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold">
                            נותרו {product.stock_quantity}
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-4">
                        <h3 className="text-base font-bold text-gray-900 mb-1 line-clamp-2 min-h-[2.5rem]">
                          {product.name}
                        </h3>
                        
                        <p className="text-xs text-gray-500 mb-3">
                          {product.sku}
                        </p>

                        {/* Price Section */}
                        <div className="border-t border-gray-100 pt-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-gray-500 line-through">
                              {formatCurrency(regularPrice)}
                            </span>
                            <span className="text-xs font-medium text-green-600">
                              חסכון {formatCurrency(savings)}
                            </span>
                          </div>
                          <div className="text-2xl font-bold text-gray-900">
                            {formatCurrency(specialPrice)}
                          </div>
                        </div>

                        {/* Stock Status */}
                        <div className="mt-3">
                          {product.stock_quantity > 0 ? (
                            <div className="flex items-center space-x-1 text-xs text-green-600">
                              <CheckCircleIcon className="h-4 w-4" />
                              <span>במלאי</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1 text-xs text-red-600">
                              <ExclamationCircleIcon className="h-4 w-4" />
                              <span>אזל מהמלאי</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Summary Stats */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl p-6 text-white shadow-xl">
            <GiftIcon className="h-12 w-12 mb-4 opacity-80" />
            <div className="text-4xl font-bold mb-2">{bundles.length}</div>
            <div className="text-purple-100">חבילות זמינות</div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-xl">
            <SparklesIcon className="h-12 w-12 mb-4 opacity-80" />
            <div className="text-4xl font-bold mb-2">{specialOffers.length}</div>
            <div className="text-orange-100">מוצרים במבצע</div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl p-6 text-white shadow-xl">
            <TruckIcon className="h-12 w-12 mb-4 opacity-80" />
            <div className="text-4xl font-bold mb-2">משלוח חינם</div>
            <div className="text-blue-100">על כל החבילות</div>
          </div>
        </div>

        {/* Bundle Details Modal */}
        {showBundleDetails && selectedBundle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">{selectedBundle.title}</h2>
                    <p className="text-purple-100 mt-1">
                      חיסכון של {selectedBundle.discount_percentage}% - {formatCurrency(selectedBundle.regular_price - selectedBundle.bundle_price)}
                    </p>
                  </div>
                  <button
                    onClick={closeBundleDetails}
                    className="text-white hover:text-purple-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Bundle Info */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">
                        {formatCurrency(selectedBundle.bundle_price)}
                      </span>
                      <span className="text-lg text-gray-500 line-through mr-2">
                        {formatCurrency(selectedBundle.regular_price)}
                      </span>
                    </div>
                    <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                      חיסכון {selectedBundle.discount_percentage}%
                    </div>
                  </div>
                  
                  {selectedBundle.description && (
                    <p className="text-gray-600 mb-4">{selectedBundle.description}</p>
                  )}
                </div>

                {/* Products List */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">מוצרים בחבילה:</h3>
                  <div className="space-y-4">
                    {selectedBundle.products?.map((product, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <GiftIcon className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900">{product.name}</h4>
                          <p className="text-sm text-gray-600">כמות: {product.quantity}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500 line-through">
                            {formatCurrency(product.original_price)}
                          </div>
                          <div className="font-bold text-purple-600">
                            {formatCurrency(product.bundle_price)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bundle Validity */}
                {selectedBundle.valid_until && (
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center gap-2 text-orange-800">
                      <ClockIcon className="h-5 w-5" />
                      <span className="font-medium">
                        החבילה בתוקף עד: {new Date(selectedBundle.valid_until).toLocaleDateString('he-IL')}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={closeBundleDetails}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  סגור
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerBundlesDiscounts;

